/**
 * Client Portal API
 * Client Portal OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {Error} from '../models/Error';
import type {Role} from '../models/Role';

export interface DeleteUserRole$Request {
    userId: number;
}

export interface DeleteUserRole$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const deleteUserRoleRequest = createAction<PayloadData<void, DeleteUserRole$Request>>('USER_ROLE__DELETE_USER_ROLE__REQUEST');
export const deleteUserRoleSuccess = createAction<PayloadData<void, DeleteUserRole$Request>>('USER_ROLE__DELETE_USER_ROLE__SUCCESS');
export const deleteUserRoleFailure = createAction<PayloadData<Error, DeleteUserRole$Request, true>>('USER_ROLE__DELETE_USER_ROLE__FAILURE');

export const deleteUserRoleActions = [
    deleteUserRoleRequest,
    deleteUserRoleSuccess,
    deleteUserRoleFailure
] as const;

/**
 * Remove role assigned for the user. It will lead to removal of all resources assigned to the user via this role. After this operation user will have no access to EVA. 
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 * @deprecated
 */
export const deleteUserRole = (meta: DeleteUserRole$Request, config?: DeleteUserRole$Config) => {
    return {
        CALL_API: {
            types: deleteUserRoleActions,
            method: ApiMethods.DELETE,
            endpoint: `/web-api/users/${meta.userId}/roles`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetAvailableRoles$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getAvailableRolesRequest = createAction<PayloadData<void>>('USER_ROLE__GET_AVAILABLE_ROLES__REQUEST');
export const getAvailableRolesSuccess = createAction<PayloadData<Array<Role>>>('USER_ROLE__GET_AVAILABLE_ROLES__SUCCESS');
export const getAvailableRolesFailure = createAction<PayloadData<Error, void, true>>('USER_ROLE__GET_AVAILABLE_ROLES__FAILURE');

export const getAvailableRolesActions = [
    getAvailableRolesRequest,
    getAvailableRolesSuccess,
    getAvailableRolesFailure
] as const;

/**
 * Get available roles for the current editor without a specific target user
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getAvailableRoles = (config?: GetAvailableRoles$Config) => {
    return {
        CALL_API: {
            types: getAvailableRolesActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/roles/available`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        }
    }
};

export interface GetAvailableUserRoles$Request {
    userId: number;
}

export interface GetAvailableUserRoles$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getAvailableUserRolesRequest = createAction<PayloadData<void, GetAvailableUserRoles$Request>>('USER_ROLE__GET_AVAILABLE_USER_ROLES__REQUEST');
export const getAvailableUserRolesSuccess = createAction<PayloadData<Array<Role>, GetAvailableUserRoles$Request>>('USER_ROLE__GET_AVAILABLE_USER_ROLES__SUCCESS');
export const getAvailableUserRolesFailure = createAction<PayloadData<Error, GetAvailableUserRoles$Request, true>>('USER_ROLE__GET_AVAILABLE_USER_ROLES__FAILURE');

export const getAvailableUserRolesActions = [
    getAvailableUserRolesRequest,
    getAvailableUserRolesSuccess,
    getAvailableUserRolesFailure
] as const;

/**
 * Get available roles for a user
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getAvailableUserRoles = (meta: GetAvailableUserRoles$Request, config?: GetAvailableUserRoles$Config) => {
    return {
        CALL_API: {
            types: getAvailableUserRolesActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/${meta.userId}/roles/available`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetUserRole$Request {
    userId: number;
}

export interface GetUserRole$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getUserRoleRequest = createAction<PayloadData<void, GetUserRole$Request>>('USER_ROLE__GET_USER_ROLE__REQUEST');
export const getUserRoleSuccess = createAction<PayloadData<Role, GetUserRole$Request>>('USER_ROLE__GET_USER_ROLE__SUCCESS');
export const getUserRoleFailure = createAction<PayloadData<Error, GetUserRole$Request, true>>('USER_ROLE__GET_USER_ROLE__FAILURE');

export const getUserRoleActions = [
    getUserRoleRequest,
    getUserRoleSuccess,
    getUserRoleFailure
] as const;

/**
 * Get role assigned for the user. User can have only one role in EVA. 
 * @param {number} meta.userId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getUserRole = (meta: GetUserRole$Request, config?: GetUserRole$Config) => {
    return {
        CALL_API: {
            types: getUserRoleActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/users/${meta.userId}/roles`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateUserRole$Request {
    userId: number;
    roleId: string;
}

export interface UpdateUserRole$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateUserRoleRequest = createAction<PayloadData<void, UpdateUserRole$Request>>('USER_ROLE__UPDATE_USER_ROLE__REQUEST');
export const updateUserRoleSuccess = createAction<PayloadData<void, UpdateUserRole$Request>>('USER_ROLE__UPDATE_USER_ROLE__SUCCESS');
export const updateUserRoleFailure = createAction<PayloadData<Error, UpdateUserRole$Request, true>>('USER_ROLE__UPDATE_USER_ROLE__FAILURE');

export const updateUserRoleActions = [
    updateUserRoleRequest,
    updateUserRoleSuccess,
    updateUserRoleFailure
] as const;

/**
 * Assign role to user. User can have only one role in EVA. 
 * @param {number} meta.userId
 * @param {string} meta.roleId
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateUserRole = (meta: UpdateUserRole$Request, config?: UpdateUserRole$Config) => {
    return {
        CALL_API: {
            types: updateUserRoleActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/users/${meta.userId}/roles/${meta.roleId}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

